import { seededRandom } from "@/lib/sketch-stroke/seeded-random";

type Point = { x: number; y: number };

/** A bowed pen travel: a cubic from `start` to `end` that keeps its `side`
 * when the destination moves mid-flight. */
export interface PenTravelPath {
  start: Point;
  c1: Point;
  c2: Point;
  end: Point;
  side: 1 | -1;
}

const PEN_TRAVEL_BEND_RATIO = 0.16;
const PEN_TRAVEL_MAX_BEND_PX = 140;

export function cursorDriftOffset(
  angle: number,
  enabled: boolean,
): { x: number; y: number } {
  return enabled
    ? {
        x: Math.sin(angle * 1.3) * 5 + Math.sin(angle * 0.7) * 3,
        y: Math.cos(angle) * 4 + Math.sin(angle * 1.1) * 2,
      }
    : { x: 0, y: 0 };
}

const IDLE_SCATTER_FAN: Array<{ x: number; y: number }> = [
  { x: 36, y: 22 },
  { x: 52, y: -108 },
  { x: 62, y: 166 },
  { x: -164, y: -72 },
  { x: -172, y: 96 },
  { x: 210, y: -20 },
  { x: 218, y: 98 },
  { x: -232, y: 16 },
];
const IDLE_SCATTER_OVERFLOW_RADIUS = 260;
const IDLE_SCATTER_RING_GAP = 112;
const IDLE_SCATTER_SLOTS = 8;
const IDLE_SCATTER_Y_SCALE = 0.76;

export function easeInOutCubic(t: number): number {
  return t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;
}

export function cubicBezierPoint(
  p0: Point,
  c1: Point,
  c2: Point,
  p3: Point,
  t: number,
): Point {
  const mt = 1 - t;
  const a = mt * mt * mt;
  const b = 3 * mt * mt * t;
  const c = 3 * mt * t * t;
  const d = t * t * t;
  return {
    x: a * p0.x + b * c1.x + c * c2.x + d * p3.x,
    y: a * p0.y + b * c1.y + c * c2.y + d * p3.y,
  };
}

/** Time constant for pointer-follow damping — shared by the workspace
 *  cursor animation and the desktop compact cursor so both follow the
 *  user's pointer with the same feel. */
export const FLUID_POINTER_DAMPING_SECONDS = 0.15;

export function dampPoint(
  current: { x: number; y: number },
  target: { x: number; y: number },
  smoothingSeconds: number,
  deltaMs: number,
): { x: number; y: number } {
  if (smoothingSeconds <= 0 || deltaMs <= 0) return target;

  const factor = 1 - Math.exp(-deltaMs / (smoothingSeconds * 1000));
  return {
    x: current.x + (target.x - current.x) * factor,
    y: current.y + (target.y - current.y) * factor,
  };
}

/** A hand's travel bows to one side: control 1 sits at 30 % of the chord with
 * the full bend, control 2 at 75 % with half of it. */
export function penTravelControls(
  start: Point,
  end: Point,
  side: 1 | -1,
): Pick<PenTravelPath, "c1" | "c2"> {
  const dx = end.x - start.x;
  const dy = end.y - start.y;
  const length = Math.hypot(dx, dy) || 1;
  const bend =
    Math.min(length * PEN_TRAVEL_BEND_RATIO, PEN_TRAVEL_MAX_BEND_PX) * side;
  const normalX = (-dy / length) * bend;
  const normalY = (dx / length) * bend;
  return {
    c1: { x: start.x + dx * 0.3 + normalX, y: start.y + dy * 0.3 + normalY },
    c2: {
      x: start.x + dx * 0.75 + normalX * 0.5,
      y: start.y + dy * 0.75 + normalY * 0.5,
    },
  };
}

/** Which way a travel bows, stable for its key (completion token or settle
 * key) or, without one, for its rounded endpoints. */
export function penTravelSide(
  key: string | undefined,
  start: Point,
  end: Point,
): 1 | -1 {
  const seed =
    key ??
    [start.x, start.y, end.x, end.y].map((value) => Math.round(value)).join();
  return seededRandom(seed)() < 0.5 ? -1 : 1;
}

/** A bowed travel from `start` to `end`; `key` seeds its side. */
export function penTravel(
  start: Point,
  end: Point,
  key: string | undefined,
): PenTravelPath {
  const side = penTravelSide(key, start, end);
  return { start, ...penTravelControls(start, end, side), end, side };
}

/** The point at (already eased) progress `t` along a travel. */
export function penTravelPoint(travel: PenTravelPath, t: number): Point {
  return cubicBezierPoint(travel.start, travel.c1, travel.c2, travel.end, t);
}

export function getIdleScatterOffset(bandIndex: number): {
  x: number;
  y: number;
} {
  if (bandIndex < IDLE_SCATTER_FAN.length) {
    return IDLE_SCATTER_FAN[bandIndex]!;
  }

  const overflowIndex = bandIndex - IDLE_SCATTER_FAN.length;
  const ring = Math.floor(overflowIndex / IDLE_SCATTER_SLOTS);
  const slot = overflowIndex % IDLE_SCATTER_SLOTS;
  const angle =
    -Math.PI / 2 +
    slot * ((Math.PI * 2) / IDLE_SCATTER_SLOTS) +
    ring * 2.399963229728653;
  const radius = IDLE_SCATTER_OVERFLOW_RADIUS + ring * IDLE_SCATTER_RING_GAP;

  return {
    x: Math.cos(angle) * radius,
    y: Math.sin(angle) * radius * IDLE_SCATTER_Y_SCALE,
  };
}
